// src/components/AdvisorDock.jsx
import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { useAdvisor } from "../AdvisorContext";
import "./AdvisorDock.css";

/**
 * AdvisorDock
 *
 * NOTE:
 * This component is now the visible OASYS Copilot dock.
 * The internal file/component name remains AdvisorDock temporarily
 * to avoid unnecessary churn while the Advisor system is converted into
 * the official OASYS Copilot layer.
 *
 * - Uses AdvisorContext for messages/streaming.
 * - Renders typing dots during empty-stream assistant shells.
 * - Shows conversational followup chips after latest assistant reply.
 *
 * Props:
 *  - mode: "dashboard" | "compliance" | "connectors" | "sop" | "billing" | "subscribe" | ...
 *  - placement: "inline" | "floating"  (default "floating")
 *  - draggable: boolean                 (default false)
 *  - rightOffset: number (px)           (default 18)
 *  - bottomOffset: number (px)          (default 18)
 *  - onPositionChange?: ({ right, bottom }) => void
 */

const POS_KEY = "oasys.advisorDock.pos.v2";
const FLOATING_DOCK_FALLBACK_WIDTH = 540;

/* ────────────────────────────────────────────────────────────
   Lightweight renderer (paragraphs only; supports [label](url))
   ──────────────────────────────────────────────────────────── */
function Linkified({ text }) {
  const lines = String(text || "").split(/\n/);

  const renderInlineWithLinks = (s, keyBase = "p") => {
    const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIdx = 0;
    let m;
    let idx = 0;

    while ((m = linkRe.exec(s)) !== null) {
      const [full, label, href] = m;
      if (m.index > lastIdx) {
        parts.push(<span key={`${keyBase}-t-${idx}`}>{s.slice(lastIdx, m.index)}</span>);
      }
      parts.push(
        <a key={`${keyBase}-a-${idx}`} href={href}>
          {label}
        </a>
      );
      lastIdx = m.index + full.length;
      idx += 1;
    }

    if (lastIdx < s.length) {
      parts.push(<span key={`${keyBase}-t-end`}>{s.slice(lastIdx)}</span>);
    }

    return parts;
  };

  return (
    <>
      {lines.map((ln, i) => (
        <p key={`p-${i}`} className="msg-paragraph">
          {renderInlineWithLinks(ln.replace(/^\s*-\s+/, ""), `p-${i}`)}
        </p>
      ))}
    </>
  );
}

const CANONICAL_PATHWAY_PROVIDER_LABELS = {
  cf: "Cloudflare",
  cloudflare: "Cloudflare",
  gh: "GitHub",
  github: "GitHub",
  s3: "Amazon S3",
  iam: "AWS IAM",
  gw: "Google Workspace",
  okta: "Okta",
};

const CANONICAL_PATHWAY_TOKEN_LABELS = {
  waf: "WAF",
  tls: "TLS",
  https: "HTTPS",
  mfa: "MFA",
  api: "API",
  acl: "ACL",
  dnssec: "DNSSEC",
  "2sv": "2SV",
  s3: "S3",
  iam: "IAM",
};

const CANONICAL_PATHWAY_SCOPE_TOKENS = new Set([
  "edge",
  "cloud",
  "security",
  "posture",
  "remediation",
]);

function formatCanonicalPathwayLabel(value) {
  const raw = String(value || "").trim();
  if (!raw.toLowerCase().startsWith("canonical.")) return "";

  const segments = raw.split(".").filter(Boolean);
  const bodySegments = segments.slice(1);

  let version = "";
  if (
    bodySegments.length &&
    /^v\d+$/i.test(bodySegments[bodySegments.length - 1])
  ) {
    version = bodySegments.pop();
  }

  const tokens = bodySegments
    .flatMap((segment) => String(segment).split("_"))
    .map((token) => token.trim())
    .filter(Boolean);

  let providerLabel = "";
  let providerToken = "";

  for (const token of tokens) {
    const normalized = token.toLowerCase();
    if (CANONICAL_PATHWAY_PROVIDER_LABELS[normalized]) {
      providerToken = normalized;
      providerLabel = CANONICAL_PATHWAY_PROVIDER_LABELS[normalized];
      break;
    }
  }

  const words = [];
  let providerConsumed = false;

  for (const token of tokens) {
    const normalized = token.toLowerCase();

    if (CANONICAL_PATHWAY_SCOPE_TOKENS.has(normalized)) {
      continue;
    }

    if (
      providerToken &&
      CANONICAL_PATHWAY_PROVIDER_LABELS[normalized] === providerLabel
    ) {
      if (!providerConsumed) {
        providerConsumed = true;
      }
      continue;
    }

    words.push(
      CANONICAL_PATHWAY_TOKEN_LABELS[normalized] ||
        `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
    );
  }

  return [providerLabel, ...words, version ? version.toLowerCase() : ""]
    .filter(Boolean)
    .join(" ");
}

function formatCardItem(item) {
  if (item === null || item === undefined || item === "") return "";

  if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
    return String(item);
  }

  if (typeof item === "object") {
    const parts = [];
    const type = item.type || item.kind || item.source || "record";
    const id = item.id || item.control_id || item.rule_id || item.finding_id || item.resource_id || item.name;

    if (type) parts.push(humanizeValue(type));

    if (id) {
      const rawId = String(id);
      const canonicalPathwayLabel =
        String(type || "").toLowerCase().includes("remediation_pathway") ||
        rawId.toLowerCase().startsWith("canonical.")
          ? formatCanonicalPathwayLabel(rawId)
          : "";

      parts.push(canonicalPathwayLabel || humanizeValue(rawId));
    }

    if (item.item_count !== undefined && item.item_label) {
      const itemCount = Number(item.item_count);
      const label = String(item.item_label || "items");
      parts.push(`${item.item_count} ${itemCount === 1 ? label.replace(/s$/, "") : label}`);
    } else if (item.records !== undefined) {
      parts.push(`${item.records} record${Number(item.records) === 1 ? "" : "s"}`);
    }

    if (item.status !== undefined) {
      const statusText = item.status === true ? "loaded" : item.status === false ? "unavailable" : String(item.status);
      parts.push(statusText);
    }

    return parts.length ? parts.join(" · ") : JSON.stringify(item);
  }

  return String(item);
}

function SectionList({ items }) {
  const safeItems = Array.isArray(items) ? items.map(formatCardItem).filter(Boolean) : [];
  if (!safeItems.length) return <div className="copilot-card-empty">None provided.</div>;

  return (
    <ul className="copilot-card-list">
      {safeItems.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  );
}

function CopilotResponseCard({ card, focus = null }) {
  if (!card || typeof card !== "object") return null;

  const confidence = String(card.confidence || "unknown").toLowerCase();

  return (
    <article className="copilot-response-card" aria-label="OASYS Copilot response card">
      <header className="copilot-card-header">
        <div>
          <h3>Guidance</h3>
          {focus?.label ? (
            <div className="copilot-card-focus">
              <span>For</span>
              <strong>{focus.label}</strong>
              {focus.type ? <span>· {focus.type}</span> : null}
            </div>
          ) : null}
        </div>
        <span className={`copilot-confidence copilot-confidence--${confidence}`}>
          {humanizeValue(confidence || "unknown")} confidence
        </span>
      </header>

      {card.summary ? (
        <section className="copilot-card-section copilot-card-section--summary">
          <h4>Summary</h4>
          <p>{card.summary}</p>
        </section>
      ) : null}

      {Array.isArray(card.recommended_actions) && card.recommended_actions.length ? (
        <section className="copilot-card-section copilot-card-section--actions">
          <h4>Recommended Actions</h4>
          <SectionList items={card.recommended_actions} />
        </section>
      ) : null}

      {card.reasoning ? (
        <details className="copilot-card-section copilot-card-section--why">
          <summary>
            <span>Why this guidance?</span>
            <span className="copilot-card-disclosure-icon" aria-hidden="true">+</span>
          </summary>
          <p>{card.reasoning}</p>
        </details>
      ) : null}

      <section className="copilot-card-section">
        <h4>Evidence Used</h4>
        <SectionList items={card.evidence_used} />
      </section>

      {(Array.isArray(card.affected_findings) && card.affected_findings.length) ||
      (Array.isArray(card.affected_controls) && card.affected_controls.length) ||
      card.affected_connector ? (
        <section className="copilot-card-section copilot-card-section--linked">
          <h4>Linked Records</h4>
          {Array.isArray(card.affected_findings) && card.affected_findings.length ? (
            <div className="copilot-linked-row"><strong>Findings:</strong> <SectionList items={card.affected_findings} /></div>
          ) : null}
          {Array.isArray(card.affected_controls) && card.affected_controls.length ? (
            <div className="copilot-linked-row"><strong>Controls:</strong> <SectionList items={card.affected_controls} /></div>
          ) : null}
          {card.affected_connector ? (
            <div className="copilot-linked-row"><strong>Connector:</strong> {formatCardItem(card.affected_connector)}</div>
          ) : null}
        </section>
      ) : null}

      {Array.isArray(card.known_limitations) && card.known_limitations.length ? (
        <details className="copilot-card-section copilot-card-section--limitations copilot-card-section--limitations-collapsible">
          <summary>
            <span className="copilot-limitations-summary-label">
              <span>Known Limitations</span>
              <span className="copilot-limitations-count">
                {card.known_limitations.length}
              </span>
            </span>
            <span className="copilot-card-disclosure-icon" aria-hidden="true">+</span>
          </summary>
          <div className="copilot-limitations-body">
            <SectionList items={card.known_limitations} />
          </div>
        </details>
      ) : null}

    </article>
  );
}


function copilotIntro() {
  return "Ask me anything about what you're working on.";
}

const COPILOT_INPUT_PLACEHOLDER = "Type or ask me anything…";


// Normalize in-app links to HashRouter format (e.g. "#/compliance?...")
function toHashRouterHref(href) {
  let h = href.startsWith("#") ? href.slice(1) : href;
  if (!h.startsWith("/")) h = "/" + h;
  return "#" + h;
}


function humanizeValue(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}


function sameCopilotFocus(left, right) {
  if (!left || !right) return false;

  const leftKey = String(left.selectionKey || "").trim();
  const rightKey = String(right.selectionKey || "").trim();

  if (leftKey && rightKey) {
    return leftKey === rightKey;
  }

  return (
    String(left.label || "").trim() === String(right.label || "").trim() &&
    String(left.type || "").trim() === String(right.type || "").trim()
  );
}

export default function AdvisorDock({
  mode = "dashboard",
  // NOTE: we purposely ignore any passed themeMode and force light tokens.
  placement = "floating",
  draggable = false,
  rightOffset = 18,
  bottomOffset = 18,
  onPositionChange,
  onClose,
}) {
  const {
    messages,
    isStreaming,
    error,
    sendChip,
    sendMessage,
    pushAssistantMessage,
    onNavigate,
    copilotFocus,
    copilotContext,
    contextSuggestions,
    availableCopilotWorkflowActions,
    runCopilotWorkflowAction,
  } = useAdvisor();

  // Keep the existing public prop contract while prompt chips are driven only by grounded context.
  void mode;

  // Stable enterprise Copilot identity.
  const copilotName = "AI Assistant";

  // Force LIGHT theme text colors.
  const nameColor = "#0b1020";

  // ────────────────────────────────────────────────────────────
  // Local input + scroll management
  // ────────────────────────────────────────────────────────────
  const [input, setInput] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const toastTimerRef = useRef(null);
  const scrollRef = useRef(null);
  const transcriptBottomRef = useRef(null);
  const latestExchangeRef = useRef(null);
  const inputRef = useRef(null);
  const chipLockRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const bottom = transcriptBottomRef.current;
      if (bottom?.scrollIntoView) {
        bottom.scrollIntoView({ block: "end", behavior: "smooth" });
        return;
      }

      const el = scrollRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    });
  }, []);

  const scrollToLatestExchange = useCallback(() => {
    requestAnimationFrame(() => {
      const target = latestExchangeRef.current;
      if (target?.scrollIntoView) {
        target.scrollIntoView({ block: "start", behavior: "smooth" });
        return;
      }

      scrollToBottom();
    });
  }, [scrollToBottom]);

  useEffect(() => {
    const last = messages[messages.length - 1];
    const previous = messages[messages.length - 2];

    if (previous?.role === "user" && last?.role === "assistant") {
      scrollToLatestExchange();
      return;
    }

    scrollToBottom();
  }, [messages, isStreaming, scrollToBottom, scrollToLatestExchange]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const showToast = useCallback((message) => {
    const nextMessage = String(message || "").trim();
    if (!nextMessage) return;

    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    setToastMessage(nextMessage);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage("");
      toastTimerRef.current = null;
    }, 2600);
  }, []);

  // Kept for future intro behavior if needed.
  const hasInteracted = useMemo(() => messages.some((m) => m.role === "user"), [messages]);
  void hasInteracted;

  // Followup suggestions are contextual only. Connector setup intentionally
  // starts with a clean conversational surface instead of unrelated risk/workload chips.
  const followups = useMemo(() => {
    const contextMode = String(copilotContext?.context_mode || "")
      .trim()
      .toLowerCase();

    if (contextMode === "connector_setup") {
      return [];
    }

    if (Array.isArray(contextSuggestions) && contextSuggestions.length) {
      return contextSuggestions.slice(0, 3);
    }

    return [];
  }, [contextSuggestions, copilotContext?.context_mode]);

  const availableWorkflowActionMap = useMemo(() => {
    const entries = Array.isArray(availableCopilotWorkflowActions)
      ? availableCopilotWorkflowActions
      : [];

    return new Map(
      entries
        .filter((action) => action?.id)
        .map((action) => [String(action.id), action])
    );
  }, [availableCopilotWorkflowActions]);

  const isWorkflowActionAvailable = useCallback(
    (action = {}) => {
      const workflowId = action.workflowActionId || action.workflow_action_id || action.actionId || action.action_id;
      if (!workflowId) return false;

      const registered = availableWorkflowActionMap.get(String(workflowId));
      return Boolean(registered && registered.enabled !== false);
    },
    [availableWorkflowActionMap]
  );


  // Intro copy stays generic; page context is handled by the backend.
  const introText = useMemo(() => copilotIntro(), []);

  // Send handlers.
  const handleSend = useCallback(
    async (value) => {
      const q = (value ?? input).trim();
      if (!q || isStreaming) return;

      setInput("");
      await sendMessage({ text: q });

      requestAnimationFrame(() => {
        inputRef.current?.focus();
        scrollToBottom();
      });
    },
    [input, isStreaming, sendMessage, scrollToBottom]
  );

  const handleChip = useCallback(
    (text) => {
      if (isStreaming || chipLockRef.current) return;

      chipLockRef.current = true;
      sendChip(text)
        .catch(() => {})
        .finally(() => {
          setTimeout(() => {
            chipLockRef.current = false;
          }, 500);

          requestAnimationFrame(() => {
            inputRef.current?.focus();
            scrollToLatestExchange();
          });
        });
    },
    [isStreaming, sendChip, scrollToLatestExchange]
  );

  const handleMessageAction = useCallback(
    async (action = {}) => {
      if (!action || isStreaming) return;

      if (action.type === "navigate" && action.href) {
        const normalized = toHashRouterHref(action.href);

        if (typeof onNavigate === "function") {
          onNavigate(normalized);
          return;
        }

        if (typeof window !== "undefined") {
          window.location.hash = normalized.replace(/^#/, "");
        }

        return;
      }

      if (action.type === "workflow") {
        const workflowId =
          action.workflowActionId ||
          action.workflow_action_id ||
          action.actionId ||
          action.action_id ||
          "";

        if (!workflowId) {
          showToast("This Copilot action is missing a workflow handler.");
          return;
        }

        if (!isWorkflowActionAvailable(action)) {
          showToast(action.unavailableMessage || "This Copilot action is not available for the current task state.");
          return;
        }

        try {
          await runCopilotWorkflowAction(workflowId, {
            ...(action.payload || {}),
            source: "copilot_action_button",
            action_id: action.id || workflowId,
          });

          showToast(action.successMessage || "Copilot workflow action completed.");

          const successAssistantMessage = String(action.successAssistantMessage || "").trim();
          const successActions = Array.isArray(action.successActions) ? action.successActions : [];

          if (typeof pushAssistantMessage === "function" && (successAssistantMessage || successActions.length)) {
            pushAssistantMessage(
              successAssistantMessage || action.successMessage || "Copilot workflow action completed.",
              { actions: successActions }
            );
          }
        } catch (e) {
          console.warn("Copilot workflow action failed:", e);
          showToast(action.errorMessage || String(e?.message || e || "Copilot workflow action failed."));
        }

        return;
      }

      if (action.type === "copy") {
        const actionLabel = String(action.label || "").trim();
        const isEvidenceChecklistCopy =
          String(action.id || "").toLowerCase().includes("evidence") ||
          /copy\s+evidence\s+checklist/i.test(actionLabel);

        const successMessage =
          action.successMessage ||
          action.confirmation ||
          (isEvidenceChecklistCopy
            ? "Evidence checklist copied to clipboard."
            : actionLabel
            ? `${actionLabel.replace(/^copy\s+/i, "")} copied to clipboard.`
            : "Copied to clipboard.");

        try {
          if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(String(action.text || ""));
            showToast(successMessage);
            return;
          }

          showToast("Clipboard copy is unavailable in this browser.");
        } catch (e) {
          console.warn("Copilot action copy failed:", e);
          showToast("I could not copy that checklist automatically.");
        }
      }
    },
    [
      isStreaming,
      onNavigate,
      showToast,
      isWorkflowActionAvailable,
      runCopilotWorkflowAction,
      pushAssistantMessage,
    ]
  );

  // Intercept clicks on anchors inside the transcript.
  const onTranscriptClick = useCallback(
    (e) => {
      const a = e.target.closest("a");
      if (!a) return;

      const rawHref = a.getAttribute("href") || "";
      const isHash = rawHref.startsWith("#");
      const isPath = rawHref.startsWith("/");

      if (isHash || isPath) {
        e.preventDefault();

        const normalized = toHashRouterHref(rawHref);

        if (typeof onNavigate === "function") {
          onNavigate(normalized);
          return;
        }

        window.location.hash = normalized.replace(/^#/, "");
        return;
      }

      // External: same tab.
      a.removeAttribute("target");
      a.removeAttribute("rel");
    },
    [onNavigate]
  );

  // ────────────────────────────────────────────────────────────
  // Drag + persisted position (floating only)
  // ────────────────────────────────────────────────────────────
  const wrapRef = useRef(null);
  const dragState = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    startRight: rightOffset,
    startBottom: bottomOffset,
  });

  const readPersistedPos = useCallback(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      if (!raw) return null;

      const obj = JSON.parse(raw);
      if (obj && typeof obj.right === "number" && typeof obj.bottom === "number") {
        return obj;
      }

      return null;
    } catch {
      return null;
    }
  }, []);

  const savePersistedPos = useCallback((pos) => {
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(pos));
    } catch {}
  }, []);

  // Initial position: persisted if present, else props.
  const initial = readPersistedPos() || {
    right: rightOffset,
    bottom: Math.max(bottomOffset, 64),
  };

  const [livePos, setLivePos] = useState(initial);

  useEffect(() => {
    const persisted = readPersistedPos();
    if (!persisted) {
      setLivePos({
        right: rightOffset,
        bottom: Math.max(bottomOffset, 64),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightOffset, bottomOffset]);

  // Keep dock inside viewport.
  const clampPos = useCallback((right, bottom) => {
    const el = wrapRef.current;
    const ww = window.innerWidth;
    const wh = window.innerHeight;
    const rect = el ? el.getBoundingClientRect() : { width: FLOATING_DOCK_FALLBACK_WIDTH, height: 420 };

    const minRight = 8;
    const minBottom = 64;
    const maxRight = Math.max(minRight, ww - rect.width - 8);
    const maxBottom = Math.max(minBottom, wh - rect.height - 8);

    return {
      right: Math.min(Math.max(right, minRight), maxRight),
      bottom: Math.min(Math.max(bottom, minBottom), maxBottom),
    };
  }, []);

  useEffect(() => {
    const onResize = () => setLivePos((p) => clampPos(p.right, p.bottom));

    // Clamp once on mount as well as on resize so a persisted position from the
    // narrower dock cannot leave the wider Copilot panel outside the viewport.
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampPos]);

  const onDragStart = (clientX, clientY) => {
    if (!draggable || placement !== "floating") return;

    dragState.current = {
      dragging: true,
      startX: clientX,
      startY: clientY,
      startRight: livePos.right,
      startBottom: livePos.bottom,
    };

    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onDragEnd);
    document.body.style.userSelect = "none";
  };

  const onDragMove = (e) => {
    if (!dragState.current.dragging) return;

    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    const nextRight = dragState.current.startRight - dx;
    const nextBottom = dragState.current.startBottom - dy;

    setLivePos(clampPos(nextRight, nextBottom));
  };

  const onTouchMove = (e) => {
    if (!dragState.current.dragging) return;

    e.preventDefault();

    const t = e.touches[0];
    if (!t) return;

    onDragMove({ clientX: t.clientX, clientY: t.clientY });
  };

  const onDragEnd = () => {
    if (!dragState.current.dragging) return;

    dragState.current.dragging = false;

    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
    window.removeEventListener("touchmove", onTouchMove);
    window.removeEventListener("touchend", onDragEnd);
    document.body.style.userSelect = "";

    savePersistedPos(livePos);

    if (typeof onPositionChange === "function") {
      onPositionChange(livePos);
    }
  };

  const handleMouseDown = (e) => {
    if (!draggable || placement !== "floating") return;
    if (e.button !== 0) return;

    onDragStart(e.clientX, e.clientY);
  };

  const handleTouchStart = (e) => {
    if (!draggable || placement !== "floating") return;

    const t = e.touches[0];
    if (!t) return;

    onDragStart(t.clientX, t.clientY);
  };

  // ────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────
  const isFloating = placement === "floating";
  const themeClass = "theme-light";

  const styleFloating = isFloating
    ? {
        position: "fixed",
        right: `${livePos.right}px`,
        bottom: `${livePos.bottom}px`,
        zIndex: 60,
      }
    : {
        position: "relative",
        right: "auto",
        bottom: "auto",
        width: "100%",
      };

  const showIntro = messages.length === 0;

  // Render conversational followups under the latest assistant bubble.
  const renderFollowups = (isLastAssistant) =>
    isLastAssistant && !isStreaming && followups.length ? (
      <div className="inline-followups" role="group" aria-label="Quick follow-ups">
        {followups.map((s, i) => (
          <button
            key={i}
            className="chip chip--inline"
            type="button"
            disabled={isStreaming}
            onClick={() => handleChip(s)}
            title={s}
          >
            {s}
          </button>
        ))}
      </div>
    ) : null;

  const renderMessageActions = (message) => {
    const actions = Array.isArray(message?.actions) ? message.actions : [];
    const visibleActions = actions.filter(
      (action) => action?.type !== "workflow" || isWorkflowActionAvailable(action)
    );

    if (!visibleActions.length) return null;

    return (
      <div className="inline-followups" role="group" aria-label="Copilot recommended actions">
        {visibleActions.map((action, i) => {
          const actionKey = action.id || action.label || `action-${i}`;

          return (
            <button
              key={actionKey}
              className="chip chip--inline"
              type="button"
              disabled={isStreaming}
              onClick={() => handleMessageAction(action)}
              title={action.title || action.label}
            >
              {action.label || "Open"}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <aside
      ref={wrapRef}
      id="advisor-dock"
      className={`advisor-dock ${themeClass} ${isFloating ? "" : "advisor-dock--inline"}`}
      style={styleFloating}
      aria-live="polite"
      aria-label="AI Assistant"
    >
      <header
        className="advisor-head"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          cursor: isFloating && draggable ? "move" : "default",
          touchAction: "none",
          mixBlendMode: "normal",
          opacity: 1,
        }}
        title={isFloating && draggable ? "Drag to reposition" : undefined}
      >
        <span className="advisor-avatar" title={copilotName} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" focusable="false">
            <path d="m12 2 1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2Z" />
            <path d="m19 16 .9 2.1L22 19l-2.1.9L19 22l-.9-2.1L16 19l2.1-.9L19 16Z" />
            <path d="m4 17 .6 1.4L6 19l-1.4.6L4 21l-.6-1.4L2 19l1.4-.6L4 17Z" />
          </svg>
        </span>

        <div className="advisor-meta" style={{ mixBlendMode: "normal", opacity: 1 }}>
          <div className="advisor-title" style={{ color: nameColor }}>
            <span className="advisor-name" style={{ color: nameColor }}>
              {copilotName}
            </span>
          </div>

          {copilotFocus?.label ? (
            <div className="advisor-focus" title={`Focused on ${copilotFocus.label}`}>
              <span className="advisor-focus-kicker">Focused on</span>
              <strong className="advisor-focus-label">{copilotFocus.label}</strong>
              {copilotFocus.type ? (
                <span className="advisor-focus-type">· {copilotFocus.type}</span>
              ) : null}
            </div>
          ) : null}
        </div>
        {isFloating && typeof onClose === "function" && (
          <button
            type="button"
            className="advisor-close-button"
            aria-label="Close AI Assistant"
            title="Close AI Assistant"
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            onClick={onClose}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </header>

      {/* Transcript */}
      <section className="advisor-transcript" ref={scrollRef} onClick={onTranscriptClick}>
        {showIntro && (
          <div className="msg msg--assistant">
            <div className="msg-bubble">
              {introText}
              {followups.length ? (
                <div className="inline-followups" role="group" aria-label="Quick starters">
                  {followups.map((s, i) => (
                    <button
                      key={i}
                      className="chip chip--inline"
                      type="button"
                      disabled={isStreaming}
                      onClick={() => handleChip(s)}
                      title={s}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {messages.map((m, idx) => {
          if (m.role === "context" && m.type === "focus_transition") {
            const transitionSuggestions = Array.isArray(m.suggestions)
              ? m.suggestions.slice(0, 3)
              : [];

            return (
              <div
                key={`focus-transition-${idx}`}
                className="copilot-focus-transition"
                role="status"
                aria-label={`Copilot focus changed to ${m.focusSnapshot?.label || "the selected work item"}`}
              >
                <div className="copilot-focus-transition-rule" aria-hidden="true" />
                <div className="copilot-focus-transition-copy">
                  <span>Focus changed</span>
                  <strong>{m.focusSnapshot?.label || "Selected work"}</strong>
                  {m.focusSnapshot?.type ? (
                    <small>· {m.focusSnapshot.type}</small>
                  ) : null}
                </div>
                <div className="copilot-focus-transition-rule" aria-hidden="true" />

                {transitionSuggestions.length ? (
                  <div
                    className="copilot-focus-transition-actions"
                    role="group"
                    aria-label="Questions for the new Copilot focus"
                  >
                    {transitionSuggestions.map((suggestion, suggestionIndex) => (
                      <button
                        key={`${suggestion}-${suggestionIndex}`}
                        className="chip chip--inline"
                        type="button"
                        disabled={isStreaming}
                        onClick={() => handleChip(suggestion)}
                        title={suggestion}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          }

          const isTypingShell =
            m.role === "assistant" &&
            m.streaming === true &&
            String(m.content || "").trim() === "";

          const isLastAssistant =
            m.role === "assistant" &&
            idx === messages.length - 1 &&
            !isTypingShell &&
            !m.streaming;

          const isLatestExchangeStart =
            m.role === "user" &&
            messages[idx + 1]?.role === "assistant" &&
            idx >= messages.length - 2;

          const historicalFocus =
            m.role === "assistant" &&
            m.focusSnapshot?.label &&
            !sameCopilotFocus(m.focusSnapshot, copilotFocus)
              ? m.focusSnapshot
              : null;

          return (
            <div
              key={idx}
              ref={isLatestExchangeStart ? latestExchangeRef : null}
              className={`msg msg--${m.role}`}
            >
              <div className={`msg-bubble${isTypingShell ? " msg-bubble--typing" : ""}`}>
                {isTypingShell ? (
                  <span
                    className="typing-dots"
                    aria-live="polite"
                    aria-label="Copilot is thinking"
                  >
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </span>
                ) : (
                  <>
                    {m.card &&
                    String(m.card?.metadata?.presentation_mode || "structured_guidance")
                      .trim()
                      .toLowerCase() === "structured_guidance" ? (
                      <CopilotResponseCard
                        card={m.card}
                        focus={historicalFocus}
                      />
                    ) : (
                      <Linkified text={m.content} />
                    )}
                    {m.streaming && String(m.content || "").trim() !== "" && (
                      <span className="typing-ellipsis" aria-hidden="true">
                        {" "}
                        …
                      </span>
                    )}
                    {renderMessageActions(m)}
                    {renderFollowups(isLastAssistant)}
                  </>
                )}
              </div>
            </div>
          );
        })}

        <div ref={transcriptBottomRef} aria-hidden="true" style={{ height: 1 }} />

        {!!error && (
          <div className="msg msg--assistant">
            <div className="msg-bubble">
              I hit a connection error while streaming that response. Please try again in a moment.
              <br />
              <small style={{ opacity: 0.7 }}>{String(error)}</small>
            </div>
          </div>
        )}
      </section>

      {toastMessage ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "absolute",
            right: 16,
            bottom: 70,
            zIndex: 2,
            maxWidth: "calc(100% - 32px)",
            border: "1px solid rgba(16, 185, 129, 0.28)",
            background: "rgba(236, 253, 245, 0.98)",
            color: "#065f46",
            borderRadius: 999,
            padding: "9px 12px",
            boxShadow: "0 14px 40px rgba(15, 23, 42, 0.18)",
            fontSize: 12,
            fontWeight: 800,
            lineHeight: 1.25,
            pointerEvents: "none",
          }}
        >
          {toastMessage}
        </div>
      ) : null}

      {/* Input */}
      <footer
        className="advisor-input"
        style={{ position: "sticky", bottom: 0 }}
      >
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={COPILOT_INPUT_PLACEHOLDER}
          title={COPILOT_INPUT_PLACEHOLDER}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSend();
            }
          }}
          aria-label="Ask OASYS Copilot"
          disabled={isStreaming}
        />

        <button
          className="advisor-send"
          onClick={() => handleSend()}
          type="button"
          disabled={isStreaming}
          aria-disabled={isStreaming}
        >
          {isStreaming ? "Sending…" : "Send"}
        </button>
      </footer>
    </aside>
  );
}